import { curveLinear, line, scaleLinear } from 'd3';
import { useRef, useState } from 'react';

import { useResizeObserver } from '../primitives';
import {
  LineChartAxisSide,
  type LineChartAxis,
  type LineChartDatum,
  type LineChartSeries,
  type ResponsiveLineChartProps,
} from './ResponsiveLineChart.types';
import {
  activePoint,
  axisWidth,
  buildYScale,
  chartLegendItems,
  chartXTicks,
  compactNumber,
  extent,
  formatterForSeries,
  legendId,
  nearestDatumForPointer,
  tooltipGroups,
  valueExtent,
} from './ResponsiveLineChart.utils';
import { ChartAxis } from './ResponsiveLineChartAxis';

const defaultChartHeight = 300;
const defaultMargins = { top: 16, right: 16, bottom: 38, left: 58 };
const maximumXAxisTicks = 8;

export function ResponsiveLineChart({
  ariaLabel,
  series,
  defaultHiddenSeriesIds = [],
  axes = [],
  valueFormatter = compactNumber,
  emptyMessage = 'No chart data available.',
  height: chartHeight = defaultChartHeight,
}: ResponsiveLineChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(containerRef);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(defaultHiddenSeriesIds));
  const [active, setActive] = useState<{
    x: number;
    label: string;
    left?: number | undefined;
    top?: number | undefined;
  } | null>(null);
  const legendItems = chartLegendItems(series);
  const visible = series.filter((item) => !hidden.has(legendId(item)));
  const data = visible.flatMap((item) => item.data);
  const visibleAxes = axes.filter((axis) => visible.some((item) => item.axisId === axis.id));
  const leftAxes = visibleAxes.filter((axis) => axis.side === LineChartAxisSide.Left);
  const rightAxes = visibleAxes.filter((axis) => axis.side === LineChartAxisSide.Right);
  const hasMultipleAxes = visibleAxes.length > 0;
  const chartWidth = hasMultipleAxes ? Math.max(width, 840) : width;
  const margins = hasMultipleAxes
    ? {
        top: 30,
        right: Math.max(16, rightAxes.length * axisWidth + 12),
        bottom: defaultMargins.bottom,
        left: Math.max(defaultMargins.left, leftAxes.length * axisWidth + 12),
      }
    : defaultMargins;
  const innerWidth = Math.max(chartWidth - margins.left - margins.right, 1);
  const innerHeight = chartHeight - margins.top - margins.bottom;
  const xDomain = extent(
    data.map((item) => item.x),
    [0, 1],
  );
  const sharedYDomain = valueExtent(
    data.map((item) => item.value),
    [0, 1],
  );
  const xScale = scaleLinear(xDomain, [0, innerWidth]);
  const sharedYScale = buildYScale(sharedYDomain, innerHeight);
  const axisScales = new Map(
    visibleAxes.map((axis) => {
      const values = visible
        .filter((item) => item.axisId === axis.id)
        .flatMap((item) => item.data.map((datum) => datum.value));
      return [axis.id, buildYScale(valueExtent(values, [0, 1]), innerHeight)] as const;
    }),
  );
  const scaleForSeries = (item: LineChartSeries) =>
    (item.axisId ? axisScales.get(item.axisId) : undefined) ?? sharedYScale;
  const xTicks = chartXTicks(
    data,
    Math.max(2, Math.min(maximumXAxisTicks, Math.floor(innerWidth / 130))),
  );
  const gridScale = axisScales.get(visibleAxes[0]?.id ?? '') ?? sharedYScale;
  const gridTicks = gridScale.ticks(4);

  const toggle = (id: string) => {
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div ref={containerRef} className="relative min-w-0">
      {/* Phones: one swipeable row instead of three wrapped lines of chips above the plot. */}
      <div
        className="mb-4 flex justify-end gap-1.5 max-sm:snap-x max-sm:[scrollbar-width:none] max-sm:justify-start max-sm:overflow-x-auto max-sm:overscroll-x-contain sm:flex-wrap [&>*]:shrink-0 [&>*]:snap-start"
        aria-label={`${ariaLabel} legend`}
      >
        {legendItems.map((item) => {
          const itemLegendId = legendId(item);
          const isHidden = hidden.has(itemLegendId);
          return (
            <button
              key={itemLegendId}
              type="button"
              className={`border-border inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${isHidden ? 'text-muted-foreground bg-transparent opacity-70' : 'bg-muted text-foreground'}`}
              aria-pressed={!isHidden}
              onClick={() => {
                toggle(itemLegendId);
              }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: item.color, opacity: isHidden ? 0.3 : 1 }}
              />
              {item.label}
            </button>
          );
        })}
      </div>
      <ul className="sr-only" aria-label={`${ariaLabel} data`}>
        {visible.flatMap((item) => {
          const formatter = formatterForSeries(item, axes, valueFormatter);
          return item.data.map((datum) => (
            <li key={`${item.id}-${String(datum.x)}`}>
              {item.label}, {datum.tooltipLabel ?? datum.label}: {formatter(datum.value)}
            </li>
          ));
        })}
      </ul>
      {width > 0 && data.length > 0 ? (
        <div className="max-w-full overflow-x-auto">
          <svg
            className="block"
            width={chartWidth}
            height={chartHeight}
            role="img"
            aria-label={ariaLabel}
          >
            <g transform={`translate(${String(margins.left)},${String(margins.top)})`}>
              {gridTicks.map((tick) => (
                <g key={tick} transform={`translate(0,${String(gridScale(tick))})`}>
                  <line className="stroke-border" x2={innerWidth} strokeDasharray="4 6" />
                  {!hasMultipleAxes ? (
                    <text
                      className="fill-muted-foreground text-[11px]"
                      x={-10}
                      dy="0.32em"
                      textAnchor="end"
                    >
                      {valueFormatter(tick)}
                    </text>
                  ) : null}
                </g>
              ))}
              {visibleAxes.map((axis) => (
                <ChartAxis
                  key={axis.id}
                  axis={axis}
                  index={
                    axis.side === LineChartAxisSide.Left
                      ? leftAxes.indexOf(axis)
                      : rightAxes.indexOf(axis)
                  }
                  innerHeight={innerHeight}
                  innerWidth={innerWidth}
                  scale={axisScales.get(axis.id) ?? sharedYScale}
                />
              ))}
              {xTicks.map((tick) => (
                <text
                  key={tick}
                  data-chart-x-tick
                  className="fill-muted-foreground text-[11px]"
                  x={xScale(tick)}
                  y={innerHeight + 24}
                  textAnchor="middle"
                >
                  {data.find((datum) => datum.x === tick)?.label}
                </text>
              ))}
              {visible.map((item) => {
                const yScale = scaleForSeries(item);
                const lineGenerator = line<LineChartDatum>()
                  .x((datum) => xScale(datum.x))
                  .y((datum) => yScale(datum.value))
                  // Straight segments: a smoothed curve invents values between buckets.
                  .curve(curveLinear);
                return (
                  <path
                    key={item.id}
                    data-series-id={item.id}
                    d={lineGenerator(item.data) ?? undefined}
                    fill="none"
                    stroke={item.color}
                    strokeDasharray={item.dashed ? '5 5' : undefined}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                  />
                );
              })}
              {visible.flatMap((item) => {
                const yScale = scaleForSeries(item);
                return item.data.map((datum) => (
                  <circle
                    key={`${item.id}-${String(datum.x)}`}
                    cx={xScale(datum.x)}
                    cy={yScale(datum.value)}
                    fill={item.color}
                    r={8}
                    fillOpacity={0}
                    aria-hidden="true"
                    onFocus={() => {
                      setActive({ x: datum.x, label: datum.label });
                    }}
                    onBlur={() => {
                      setActive(null);
                    }}
                  />
                ));
              })}
              {active ? (
                <g aria-hidden="true" pointerEvents="none">
                  <line
                    data-chart-cursor
                    className="stroke-border"
                    x1={xScale(active.x)}
                    x2={xScale(active.x)}
                    y1={0}
                    y2={innerHeight}
                  />
                  {visible.flatMap((item) => {
                    const datum = item.data.find((candidate) => candidate.x === active.x);
                    if (!datum) return [];
                    return [
                      <circle
                        key={item.id}
                        cx={xScale(datum.x)}
                        cy={scaleForSeries(item)(datum.value)}
                        r={4}
                        fill={item.color}
                        stroke="var(--card)"
                        strokeWidth={2}
                      />,
                    ];
                  })}
                </g>
              ) : null}
              <rect
                data-chart-hover-layer
                x={0}
                y={0}
                width={innerWidth}
                height={innerHeight}
                fill="transparent"
                aria-hidden="true"
                onPointerMove={(event) => {
                  const datum = nearestDatumForPointer(event, data, xScale);
                  if (datum) setActive(activePoint(event, containerRef.current, datum));
                }}
                onPointerLeave={() => {
                  setActive(null);
                }}
              />
            </g>
          </svg>
        </div>
      ) : (
        <div
          className="text-muted-foreground flex items-center justify-center text-sm"
          style={{ height: chartHeight }}
        >
          {emptyMessage}
        </div>
      )}
      {active ? (
        <ChartTooltip
          active={active}
          axes={axes}
          series={visible}
          valueFormatter={valueFormatter}
          width={width}
        />
      ) : null}
    </div>
  );
}

function ChartTooltip({
  active,
  axes,
  series,
  valueFormatter,
  width,
}: {
  active: { x: number; label: string; left?: number | undefined; top?: number | undefined };
  axes: LineChartAxis[];
  series: LineChartSeries[];
  valueFormatter: (value: number) => string;
  width: number;
}) {
  const values = series.flatMap((item) => {
    const datum = item.data.find((candidate) => candidate.x === active.x);
    return datum ? [{ item, datum }] : [];
  });
  const groups = tooltipGroups(active.label, values);
  const positioned = active.left !== undefined && active.top !== undefined;
  const style = positioned
    ? {
        left: Math.max(8, Math.min(active.left ?? 8, width - 230)),
        top: Math.max(40, active.top ?? 40),
      }
    : undefined;

  return (
    <div
      className={`border-border bg-popover/95 text-popover-foreground pointer-events-none absolute z-10 min-w-[210px] rounded-md border p-3 text-xs shadow-lg backdrop-blur ${positioned ? '' : 'top-10 right-2'}`}
      style={style}
      role="tooltip"
    >
      <div className="flex flex-col gap-3">
        {groups.map((group) => (
          <div key={group.label}>
            <p className="text-muted-foreground mb-1.5 font-medium">{group.label}</p>
            <div className="flex flex-col gap-1.5">
              {group.values.map(({ item, datum }) => (
                <div key={item.id} className="flex items-center justify-between gap-4">
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                      aria-hidden="true"
                    />
                    {item.label}
                  </span>
                  <span className="font-semibold tabular-nums">
                    {formatterForSeries(item, axes, valueFormatter)(datum.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
